import { useState, useRef, useEffect, useCallback } from "react";
import {
  RefreshCw, ChevronDown, MoreVertical, Palette,
  Edit2, PlusCircle, Trash2, X, Save, GripVertical,
  Calendar, Hash, AlertCircle, Search
} from "lucide-react";
import axiosInstance from "../../API/interceptor";
import TaskDetailPanel from "./TaskDetailPanel";

/* ══ CONSTANTS ═══════════════════════════════════════════════ */
const COLOR_PALETTE = [
  "#6366f1","#7c3aed","#06b6d4","#0891b2","#ec4899","#ef4444",
  "#3b82f6","#22d3ee","#f43f5e","#eab308","#10b981","#1e1b4b",
  "#67e8f9","#fbcfe8","#fde68a","#fca5a5","#a78bfa","#818cf8",
  "#fb7185","#c084fc","#f9a8d4","#bfdbfe","#86efac","#93c5fd",
  "#c4b5fd","#f472b6","#fb923c","#f87171","#34d399","#2dd4bf",
  "#1d4ed8","#9333ea","#9ca3af","#4b5563"
];


const PRIORITY_STYLE = {
  LOW:      { bg:"#dcfce7", color:"#15803d", border:"#86efac" },
  MEDIUM:   { bg:"#cffafe", color:"#0e7490", border:"#67e8f9" },
  HIGH:     { bg:"#fee2e2", color:"#dc2626", border:"#fca5a5" },
  URGENT:   { bg:"#fff7ed", color:"#c2410c", border:"#fed7aa" },
  CRITICAL: { bg:"#fce7f3", color:"#be185d", border:"#fbcfe8" },
};

const STATUS_STYLE = {
  "open":             { bg:"#f5f3ff", color:"#7c3aed", border:"#ddd6fe" },
  "in-progress":      { bg:"#f0fdfa", color:"#0d9488", border:"#99f6e4" },
  "in progress":      { bg:"#f0fdfa", color:"#0d9488", border:"#99f6e4" },
  "on-hold":          { bg:"#fdf2f8", color:"#db2777", border:"#f9a8d4" },
  "on hold":          { bg:"#fdf2f8", color:"#db2777", border:"#f9a8d4" },
  "completed":        { bg:"#ecfdf5", color:"#059669", border:"#a7f3d0" },
  "approval pending": { bg:"#fffbeb", color:"#d97706", border:"#fde68a" },
  "closed":           { bg:"#f1f5f9", color:"#475569", border:"#cbd5e1" },
};

const fmtDate = (v) => {
  if (!v) return "No deadline";
  const d = new Date(v);
  if (isNaN(d)) return "No deadline";
  return d.toLocaleDateString(undefined, { day:"numeric", month:"short", year:"numeric", timeZone:"UTC" });
};

/* ══ TOAST ═══════════════════════════════════════════════════ */
function Toast({ msg, type, onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 2800); return () => clearTimeout(t); }, [onDone]);
  const bg = type === "error" ? "#ef4444" : type === "warning" ? "#f59e0b" : "#10b981";
  return (
    <div className="fixed bottom-6 right-6 z-[99999] text-white rounded-xl px-[18px] py-[10px] text-[13px] font-semibold shadow-[0_4px_20px_rgba(0,0,0,0.18)] max-w-[320px]"
      style={{ background: bg }}>{msg}</div>
  );
}

/* ══ SKELETON ════════════════════════════════════════════════ */
function BoardSkeleton() {
  return (
    <div className="p-4 flex gap-4 overflow-x-auto items-start pb-10 min-h-[calc(100vh-130px)]">
      {[1,2,3].map(i => (
        <div key={i} className="shrink-0 w-[300px] rounded-2xl overflow-hidden animate-pulse bg-white shadow-[0_1px_12px_rgba(0,0,0,0.06)]">
          <div className="h-14 rounded-t-2xl bg-[#e2e8f0]"/>
          <div className="p-3 space-y-2.5">
            {[1,2].map(j => (
              <div key={j} className="rounded-xl p-3.5 bg-[#f8fafc] border border-[#f1f5f9]">
                <div className="flex gap-1.5 mb-2">
                  <div className="h-5 w-16 rounded-md bg-gray-200"/>
                  <div className="h-5 w-20 rounded-md bg-gray-200"/>
                </div>
                <div className="h-4 w-4/5 rounded bg-gray-200 mb-2"/>
                <div className="h-3 w-3/5 rounded bg-gray-100"/>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ══ EMPTY STATE ═════════════════════════════════════════════ */
function EmptyState({ canModify, onCreateFirst }) {
  return (
    <div className="flex flex-col items-center justify-center py-24 text-center">
      <div className="text-5xl mb-3">🗂️</div>
      {canModify ? (
        <>
          <p className="text-sm font-semibold text-slate-600 mb-1">No stages yet for this workspace</p>
          <p className="text-xs text-slate-400 mb-4">Create your first stage to start organizing tasks</p>
          <button onClick={onCreateFirst}
            className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-white rounded-xl transition-all hover:opacity-90 bg-gradient-to-br from-[#6366f1] to-[#8b5cf6]">
            <PlusCircle size={14}/> Create First Stage
          </button>
        </>
      ) : (
        <>
          <p className="text-sm font-semibold text-slate-600 mb-1">No stages configured</p>
          <p className="text-xs text-slate-400">Contact your workspace admin to create stages</p>
        </>
      )}
    </div>
  );
}

/* ══ MAIN COMPONENT ══════════════════════════════════════════ */
export default function KanbanBoard() {
  /* ── workspace options ── */
  const [workspaceOpts, setWorkspaceOpts] = useState([]);
  const [workspace,     setWorkspace]     = useState("");       // product_code string

  /* ── permissions & filter ── */
  const [canModify,      setCanModify]      = useState(false);
  const [canSeeAll,      setCanSeeAll]      = useState(false);
  const [taskFilter,     setTaskFilter]     = useState("self"); // "self" | "all"

  /* ── board data ── */
  const [stages,  setStages]  = useState([]);   // [{ name, color, tasks:[] }]
  const [loading, setLoading] = useState(false);
  const [empty,   setEmpty]   = useState(false);

  /* ── UI state ── */
  const [menuOpen,    setMenuOpen]    = useState(null);   // stage name
  const [colorPicker, setColorPicker] = useState(null);  // stage name
  const [modal,       setModal]       = useState(null);  // { mode:"add"|"edit", stageName? }
  const [stageName,   setStageName]   = useState("");
  const [saving,      setSaving]      = useState(false);

  /* ── drag state ── */
  const dragCol         = useRef(null);
  const dragOverCol     = useRef(null);
  const [colDragOverIdx,  setColDragOverIdx]  = useState(null);
  const [draggingColIdx,  setDraggingColIdx]  = useState(null);
  const [taskDrag,        setTaskDrag]        = useState(null);   // { taskId, fromStage }
  const [taskDropStage,   setTaskDropStage]   = useState(null);

  /* ── detail panel ── */
  const [detailTaskId, setDetailTaskId] = useState(null);

  /* ── toast ── */
  const [toast, setToast] = useState(null);
  const showToast = useCallback((msg, type="success") => setToast({ msg, type }), []);

  /* ── abort ref for cancellable requests ── */
  const loadCtrl = useRef(null);

  /* ════ LOAD WORKSPACE OPTIONS ════ */
  useEffect(() => {
    axiosInstance.get("/task_assignment/get_product_codes")
      .then(res => {
        const arr = Array.isArray(res.data?.product_codes) ? res.data.product_codes
                  : Array.isArray(res.data) ? res.data : [];
        const opts = arr.map(item => {
          if (typeof item === "object") {
            const key = Object.keys(item)[0];
            return { value: String(key), label: item[key] };
          }
          return null;
        }).filter(Boolean);
        setWorkspaceOpts(opts);
      })
      .catch(() => {});
  }, []);

  /* ════ LOAD WORK STAGES ════ */
  const loadStages = useCallback(async (wsId, filter) => {
    if (!wsId) return;

    // cancel previous in-flight
    if (loadCtrl.current) loadCtrl.current.abort();
    const ctrl = new AbortController();
    loadCtrl.current = ctrl;

    setLoading(true);
    setEmpty(false);
    setStages([]);

    try {
      const params = {};
      if (filter && filter !== "") params.filter = filter;

      const res = await axiosInstance.get(`/task_assignment/get_work_stages/${wsId}`, {
        params,
        signal: ctrl.signal,
      });

      const data = res.data;

      // permissions
      setCanModify(data.can_modify || false);
      setCanSeeAll(data.can_see_all_tasks || false);

      // if admin/owner and no filter explicitly set → default to "all"
      if (data.can_see_all_tasks && !filter) {
        setTaskFilter("all");
      }

      if (!data.success) {
        showToast(data.error || "Failed to load stages", "error");
        setEmpty(true);
        return;
      }

      if (!data.stages || data.stages.length === 0) {
        setEmpty(true);
        return;
      }

      // build stages with tasks grouped
      const tasksByStage = {};
      data.stages.forEach(s => { tasksByStage[s.name] = []; });
      (data.tasks || []).forEach(t => {
        const s = t.current_stage;
        if (s && tasksByStage[s] !== undefined) {
          tasksByStage[s].push(t);
        } else if (data.stages.length > 0) {
          tasksByStage[data.stages[0].name].push(t);
        }
      });

      setStages(data.stages.map(s => ({
        name:  s.name,
        color: s.color || "#6366f1",
        tasks: tasksByStage[s.name] || [],
      })));

    } catch (err) {
      if (err.name === "CanceledError" || err.name === "AbortError") return;
      showToast("Failed to load work stages", "error");
      setEmpty(true);
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  /* workspace change → reload */
  useEffect(() => {
    if (workspace) {
      setTaskFilter("self"); // reset filter on workspace change
      loadStages(workspace, null);
    } else {
      setStages([]);
      setEmpty(false);
      setCanModify(false);
      setCanSeeAll(false);
    }
  }, [workspace]); // eslint-disable-line

  /* filter change → reload */
  const handleFilterChange = (f) => {
    setTaskFilter(f);
    loadStages(workspace, f);
  };

  const handleRefresh = () => {
    if (!workspace) { showToast("Please select a workspace first", "warning"); return; }
    loadStages(workspace, taskFilter);
  };

  /* ════ STAGE CRUD ════ */
  const openAddModal  = () => { setStageName(""); setModal({ mode:"add" });  setMenuOpen(null); };
  const openEditModal = (name) => { setStageName(name); setModal({ mode:"edit", stageName:name }); setMenuOpen(null); };

  const saveStageModal = async () => {
    if (!stageName.trim()) return;
    setSaving(true);
    try {
      if (modal.mode === "add") {
        const isFirst = stages.length === 0;
        await axiosInstance.post("/task_assignment/add_work_stage", {
          workspace_id: workspace,
          stage_name:   stageName.trim(),
          is_first:     isFirst,
          color:        COLOR_PALETTE[Math.floor(Math.random() * COLOR_PALETTE.length)],
        });
        showToast("Stage added successfully");
      } else {
        await axiosInstance.post("/task_assignment/update_work_stage", {
          workspace_id:   workspace,
          old_stage_name: modal.stageName,
          new_stage_name: stageName.trim(),
        });
        showToast("Stage renamed successfully");
      }
      setModal(null);
      loadStages(workspace, taskFilter);
    } catch {
      showToast("Failed to save stage", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteStage = async (name) => {
    const stage = stages.find(s => s.name === name);
    if (stage?.tasks?.length > 0) {
      showToast(`Move all ${stage.tasks.length} task(s) before deleting`, "warning");
      return;
    }
    if (!window.confirm(`Delete stage "${name}"? This cannot be undone.`)) return;
    try {
      await axiosInstance.post("/task_assignment/delete_work_stage", {
        workspace_id: workspace,
        stage_name:   name,
      });
      showToast("Stage deleted");
      loadStages(workspace, taskFilter);
    } catch {
      showToast("Failed to delete stage", "error");
    }
    setMenuOpen(null);
  };

  const handleApplyColor = async (name, color) => {
    // optimistic update
    setStages(prev => prev.map(s => s.name === name ? { ...s, color } : s));
    setColorPicker(null);
    try {
      await axiosInstance.post("/task_assignment/update_stage_color", {
        workspace_id: workspace,
        stage_name:   name,
        color,
      });
      showToast("Color updated");
    } catch {
      showToast("Failed to update color", "error");
      loadStages(workspace, taskFilter); // revert
    }
  };

  /* ════ COLUMN DRAG-DROP ════ */
  const onColDragStart = (idx) => {
    if (!canModify) return;
    dragCol.current = idx;
    setDraggingColIdx(idx);
  };
  const onColDragOver  = (e, idx) => { e.preventDefault(); if (dragCol.current === null || !canModify) return; dragOverCol.current = idx; setColDragOverIdx(idx); };
  const onColDrop      = async (idx) => {
    if (dragCol.current === null || dragCol.current === idx || !canModify) return resetDrag();
    const arr = [...stages];
    const [moved] = arr.splice(dragCol.current, 1);
    arr.splice(idx, 0, moved);
    setStages(arr); // optimistic
    resetDrag();

    try {
      await axiosInstance.post("/task_assignment/reorder_stages_bulk", {
        workspace_id: workspace,
        stage_order:  arr.map(s => s.name),
      });
      showToast("Stage reordered");
    } catch {
      showToast("Failed to reorder", "error");
      loadStages(workspace, taskFilter);
    }
  };
  const resetDrag = () => { dragCol.current = null; dragOverCol.current = null; setColDragOverIdx(null); setDraggingColIdx(null); };

  /* ════ TASK DRAG-DROP ════ */
  const onTaskDragStart = (taskId, fromStage) => { dragCol.current = null; setTaskDrag({ taskId, fromStage }); };
  const onTaskDrop      = async (toStage) => {
    if (!taskDrag || taskDrag.fromStage === toStage) { setTaskDrag(null); setTaskDropStage(null); return; }

    const task = stages.find(s => s.name === taskDrag.fromStage)?.tasks.find(t => t._id === taskDrag.taskId);
    if (!task) return;

    // optimistic update
    setStages(prev => prev.map(s => {
      if (s.name === taskDrag.fromStage) return { ...s, tasks: s.tasks.filter(t => t._id !== taskDrag.taskId) };
      if (s.name === toStage)            return { ...s, tasks: [...s.tasks, { ...task, current_stage: toStage }] };
      return s;
    }));
    setTaskDrag(null); setTaskDropStage(null);

    try {
      await axiosInstance.post("/task_assignment/move_task_stage_only", {
        task_id:   taskDrag.taskId,
        new_stage: toStage,
      });
      showToast("Task moved");
    } catch {
      showToast("Failed to move task", "error");
      loadStages(workspace, taskFilter);
    }
  };

  const closeAll = () => { setMenuOpen(null); setColorPicker(null); };

  /* ════ RENDER ════ */
  return (
    <div className="bg-gray-50 select-none font-[Inter,sans-serif]" onClick={closeAll}>

      {/* Top Bar */}
      <div className="bg-white border-b border-gray-100 px-4 py-3 flex items-center gap-3 flex-wrap shadow-sm">

        {/* Workspace single-select — listing table style */}
        <div className="w-[240px]">
          <WorkspaceSingleSelect
            value={workspace}
            onChange={setWorkspace}
            options={workspaceOpts}
          />
        </div>

        {/* Refresh */}
        <button
          onClick={e => { e.stopPropagation(); handleRefresh(); }}
          disabled={!workspace || loading}
          className="w-10 h-10 rounded-lg flex items-center justify-center text-white shadow-sm transition-all hover:scale-105 active:scale-95 disabled:opacity-50 bg-gradient-to-br from-[#6366f1] to-[#8b5cf6]">
          <RefreshCw size={15} className={loading ? "animate-spin" : ""}/>
        </button>

        {/* All Team / Self filter — only for admin/product owner */}
        {workspace && canSeeAll && (
          <FilterSelectBox
            value={taskFilter}
            onChange={handleFilterChange}
            options={[
              { value:"all",  label:"All Team" },
              { value:"self", label:"My Tasks" },
            ]}
          />
        )}
      </div>

      {/* No workspace selected */}
      {!workspace && (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="text-5xl mb-3">📋</div>
          <p className="text-sm font-semibold text-slate-600 mb-1">Select a workspace to view stages</p>
          <p className="text-xs text-slate-400">Choose from the dropdown above</p>
        </div>
      )}

      {/* Loading skeleton */}
      {workspace && loading && <BoardSkeleton />}

      {/* Empty state */}
      {workspace && !loading && empty && (
        <EmptyState canModify={canModify} onCreateFirst={openAddModal} />
      )}

      {/* Board */}
      {workspace && !loading && !empty && stages.length > 0 && (
        <div className="p-4 flex gap-4 overflow-x-auto items-start pb-10 min-h-[calc(100vh-130px)]">
          {stages.map((stage, idx) => (
            <Column
              key={stage.name}
              stage={stage}
              idx={idx}
              canModify={canModify}
              isDragging={draggingColIdx === idx}
              isDragOver={colDragOverIdx === idx && draggingColIdx !== idx}
              isTaskDragOver={taskDropStage === stage.name}
              menuOpen={menuOpen === stage.name}
              colorPickerOpen={colorPicker === stage.name}
              onColDragStart={() => onColDragStart(idx)}
              onColDragOver={e => onColDragOver(e, idx)}
              onColDrop={() => onColDrop(idx)}
              onColDragEnd={resetDrag}
              onTaskDragStart={onTaskDragStart}
              onTaskDragOver={e => { if (taskDrag) { e.preventDefault(); setTaskDropStage(stage.name); }}}
              onTaskDragLeave={() => setTaskDropStage(null)}
              onTaskDrop={() => onTaskDrop(stage.name)}
              onToggleMenu={e => { e.stopPropagation(); setMenuOpen(menuOpen === stage.name ? null : stage.name); setColorPicker(null); }}
              onOpenColor={() => { setColorPicker(stage.name); setMenuOpen(null); }}
              onApplyColor={(_, c) => handleApplyColor(stage.name, c)}
              onEditStage={() => openEditModal(stage.name)}
              onAddStage={openAddModal}
              onDeleteStage={() => handleDeleteStage(stage.name)}
              onCloseColor={() => setColorPicker(null)}
              onTaskClick={setDetailTaskId}
            />
          ))}

          {/* Add Stage button — only if has permission */}
          {canModify && (
            <button
              onClick={e => { e.stopPropagation(); openAddModal(); }}
              className="flex-shrink-0 w-72 h-20 rounded-2xl border-2 border-dashed border-gray-200 text-gray-400 text-sm font-medium
                         hover:border-indigo-400 hover:text-indigo-500 hover:bg-indigo-50 transition-all flex items-center justify-center gap-2">
              <PlusCircle size={18}/> Add Stage
            </button>
          )}
        </div>
      )}

      {/* Add / Edit Stage Modal */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[rgba(15,15,35,0.45)] backdrop-blur-[4px]"
          onClick={e => { if (e.target === e.currentTarget) setModal(null); }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-base font-bold text-gray-800">
                {modal.mode === "add" ? "Add New Stage" : "Edit Stage"}
              </h3>
              <button onClick={() => setModal(null)}
                className="w-9 h-9 rounded-xl flex items-center justify-center text-white bg-[#06b6d4]">
                <X size={15}/>
              </button>
            </div>
            <div className="px-6 py-5">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Stage Name <span className="text-red-500">*</span>
              </label>
              <input
                autoFocus
                type="text"
                value={stageName}
                onChange={e => setStageName(e.target.value.slice(0, 50))}
                onKeyDown={e => e.key === "Enter" && saveStageModal()}
                placeholder="e.g., Write Script, Video Editing"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none transition-all focus:border-[#6366f1]"
              />
              <p className="text-xs text-gray-400 mt-1.5 flex items-center gap-1">
                <AlertCircle size={11}/> Max 50 characters ({stageName.length}/50)
              </p>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 pb-5">
              <button onClick={() => setModal(null)}
                className="px-5 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors">
                Cancel
              </button>
              <button
                onClick={saveStageModal}
                disabled={saving || !stageName.trim()}
                className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-white rounded-xl transition-all hover:opacity-90 active:scale-95 disabled:opacity-60 bg-gradient-to-br from-[#6366f1] to-[#8b5cf6]">
                {saving
                  ? <><span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"/>&nbsp;Saving…</>
                  : <><Save size={14}/> Save Stage</>
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Task Detail Panel */}
      <TaskDetailPanel taskId={detailTaskId} onClose={() => setDetailTaskId(null)} />

      {/* Toast */}
      {toast && <Toast msg={toast.msg} type={toast.type} onDone={() => setToast(null)} />}
    </div>
  );
}

/* ══ COLUMN ══════════════════════════════════════════════════ */
function Column({
  stage, isDragging, isDragOver, isTaskDragOver, canModify,
  menuOpen, colorPickerOpen,
  onColDragStart, onColDragOver, onColDrop, onColDragEnd,
  onTaskDragStart, onTaskDragOver, onTaskDragLeave, onTaskDrop,
  onToggleMenu, onOpenColor, onApplyColor,
  onEditStage, onAddStage, onDeleteStage, onCloseColor,
  onTaskClick,
}) {
  return (
    <div
      draggable={canModify}
      onDragStart={onColDragStart}
      onDragOver={e => { onColDragOver(e); onTaskDragOver(e); }}
      onDragLeave={onTaskDragLeave}
      onDrop={e => { e.preventDefault(); onColDrop(); onTaskDrop(); }}
      onDragEnd={onColDragEnd}
      className={`shrink-0 w-[300px] h-[calc(100vh-210px)] bg-white rounded-2xl overflow-visible transition-all flex flex-col ${canModify ? "cursor-grab" : "cursor-default"}`}
      style={{
        opacity: isDragging ? 0.4 : 1,
        transform: isDragOver ? "scale(1.02)" : "scale(1)",
        border: isDragOver
          ? `2px dashed ${stage.color}`
          : isTaskDragOver
            ? `2px solid ${stage.color}`
            : "2px solid transparent",
        boxShadow: isDragging
          ? "none"
          : isDragOver || isTaskDragOver
            ? `0 0 0 4px ${stage.color}28, 0 8px 30px rgba(0,0,0,0.12)`
            : "0 1px 12px rgba(0,0,0,0.06)",
        transition:"all 0.18s ease",
      }}>

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3.5 rounded-t-2xl shrink-0"
        style={{ background:stage.color }}>
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {canModify && <GripVertical size={15} color="rgba(255,255,255,0.6)" className="flex-shrink-0"/>}
          <span className="text-white font-bold text-sm tracking-wide truncate">{stage.name}</span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="min-w-[28px] h-7 px-2 rounded-full flex items-center justify-center text-xs font-bold bg-white/25 text-white">
            {stage.tasks.length}
          </span>

          {/* 3-dot menu — only if has permission */}
          {canModify && (
            <div className="relative" onClick={e => e.stopPropagation()}>
              <button onClick={onToggleMenu}
                className="w-7 h-7 rounded-lg flex items-center justify-center transition-all hover:scale-105 bg-white/[.22]">
                <MoreVertical size={14} color="#fff"/>
              </button>
              {menuOpen && (
                <div className="absolute right-0 top-9 z-[100] w-52 bg-white rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.14)] border border-gray-100 py-2 overflow-hidden">
                  <MenuItem icon={<Palette size={15}/>}    label="Change Color"  onClick={onOpenColor}/>
                  <MenuItem icon={<Edit2 size={15}/>}      label="Edit Stage"    onClick={onEditStage}/>
                  <MenuItem icon={<PlusCircle size={15}/>} label="Add New Stage" onClick={onAddStage}/>
                  <div className="border-t border-gray-100 my-1"/>
                  <MenuItem icon={<Trash2 size={15}/>}     label="Delete Stage"  onClick={onDeleteStage} danger/>
                </div>
              )}
              {colorPickerOpen && (
                <div className="absolute right-0 top-9 z-[100] w-60 bg-white rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.14)] border border-gray-100 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-bold text-gray-700 flex items-center gap-1.5">
                      <Palette size={13} className="text-indigo-400"/> Choose Color
                    </span>
                    <button onClick={onCloseColor} className="text-gray-400 hover:text-gray-600"><X size={13}/></button>
                  </div>
                  <div className="grid grid-cols-8 gap-1.5">
                    {COLOR_PALETTE.map(c => (
                      <button key={c} onClick={() => onApplyColor(stage.name, c)}
                        className={`w-6 h-6 rounded-lg transition-all hover:scale-125 hover:shadow-md border-2 ${c === stage.color ? "border-[#1e293b]" : "border-transparent"}`}
                        style={{ background:c }}/>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Task list */}
      <div className="p-3 space-y-2.5 flex-1 min-h-36 overflow-y-auto">
        {stage.tasks.map(task => (
          <TaskCard
            key={task._id}
            task={task}
            stageName={stage.name}
            accentColor={stage.color}
            onTaskDragStart={onTaskDragStart}
            onTaskClick={onTaskClick}
          />
        ))}
        {stage.tasks.length === 0 && (
          <div className="flex flex-col items-center justify-center h-28 rounded-xl border-2 border-dashed border-gray-100 text-gray-300 text-xs gap-1.5">
            <GripVertical size={16}/>
            <span>Drop tasks here</span>
          </div>
        )}
      </div>
    </div>
  );
}

/* ══ TASK CARD ═══════════════════════════════════════════════ */
function TaskCard({ task, stageName, onTaskDragStart, onTaskClick, accentColor }) {
  const priority = (task.priority || "MEDIUM").toUpperCase();
  const status   = (task.task_status || "open").toLowerCase();
  const p = PRIORITY_STYLE[priority] || PRIORITY_STYLE.MEDIUM;
  const s = STATUS_STYLE[status]     || STATUS_STYLE["open"];

  const displayId    = task.unique_seq_id || task.task_id || "";
  const title        = task.task_title || task.task_type || "Untitled Task";
  const deadlineStr  = fmtDate(task.due_date || task.completion_sla);

  return (
    <div
      draggable
      onDragStart={e => { e.stopPropagation(); onTaskDragStart(task._id, stageName); }}
      onClick={e => { e.stopPropagation(); onTaskClick(task._id); }}
      className="rounded-xl p-3.5 transition-all hover:-translate-y-0.5 cursor-pointer bg-white border border-[#f1f5f9] shadow-[0_1px_6px_rgba(0,0,0,0.05)] hover:shadow-[0_4px_16px_rgba(0,0,0,0.1)]">
      <div className="flex gap-2.5">
        <div className="w-1 rounded-full flex-shrink-0 mt-0.5 min-h-[40px]" style={{ background:accentColor }}/>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-2 flex-wrap">
            <Badge label={priority}                   bg={p.bg} color={p.color} border={p.border}/>
            <Badge label={status.replace("-"," ")}    bg={s.bg} color={s.color} border={s.border}/>
          </div>
          <p className="text-sm font-semibold text-gray-800 leading-snug mb-3 line-clamp-2">{title}</p>
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1 text-xs text-gray-400">
              <Calendar size={11}/> {deadlineStr}
            </span>
            {displayId && (
              <span className="flex items-center gap-1 text-xs font-bold text-white px-2 py-0.5 rounded-md bg-[#06b6d4]">
                <Hash size={10}/>{displayId}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══ SMALL UI PIECES ═════════════════════════════════════════ */
function Badge({ label, bg, color, border }) {
  return (
    <span className="text-xs font-semibold px-2 py-0.5 rounded-md uppercase tracking-wide border"
      style={{ background:bg, color, borderColor:border }}>
      {label}
    </span>
  );
}

function MenuItem({ icon, label, onClick, danger }) {
  return (
    <button onClick={onClick}
      className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors ${danger ? "text-[#ef4444] hover:bg-[#fef2f2]" : "text-[#374151] hover:bg-[#f8fafc]"}`}>
      <span className={danger ? "text-[#ef4444]" : "text-[#94a3b8]"}>{icon}</span>{label}
    </button>
  );
}

/* ══ WORKSPACE SINGLE SELECT — listing table style ══════════ */
const WS_COLORS = [
  { bg:"#F3E8FF", color:"#7C3AED", border:"#DDD6FE" },
  { bg:"#E0F2FE", color:"#0284C7", border:"#BAE6FD" },
  { bg:"#FEF9C3", color:"#CA8A04", border:"#FDE68A" },
  { bg:"#DCFCE7", color:"#16A34A", border:"#BBF7D0" },
  { bg:"#FCE7F3", color:"#DB2777", border:"#F9A8D4" },
  { bg:"#FFF7ED", color:"#D97706", border:"#FDE68A" },
  { bg:"#FEF2F2", color:"#DC2626", border:"#FECACA" },
];

function WorkspaceSingleSelect({ value, onChange, options = [] }) {
  const [open,   setOpen]   = useState(false);
  const [search, setSearch] = useState("");
  const wrapRef = useRef(null);

  useEffect(() => {
    const fn = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  const getCfg = (val) => {
    const idx = options.findIndex(o => o.value === val);
    const opt = options[idx];
    const pal = WS_COLORS[(idx < 0 ? 0 : idx) % WS_COLORS.length];
    return { label: opt?.label || val, ...pal };
  };

  const filtered = options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()));
  const cfg = value ? getCfg(value) : null;

  const handleSelect = (val) => {
    onChange(val);
    setOpen(false);
    setSearch("");
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange("");
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative w-full">

      {/* Trigger */}
      <div
        onClick={() => { setSearch(""); setOpen(o => !o); }}
        className={`flex items-center justify-between gap-2 w-full bg-white border rounded-lg px-3 py-2 text-sm cursor-pointer shadow-sm transition-all ${
          open
            ? "border-violet-500 ring-4 ring-violet-50"
            : "border-slate-200 hover:border-violet-300 hover:shadow-sm"
        }`}
      >
        {!cfg ? (
          <span className="text-xs text-slate-400 flex-1">Select Workspace</span>
        ) : (
          <span
            className="inline-flex items-center gap-1 text-[10.5px] font-semibold px-2 py-0.5 rounded-full shrink-0 border-[1.5px]"
            style={{ background: cfg.bg, color: cfg.color, borderColor: cfg.border }}
          >
            <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: cfg.color }} />
            {cfg.label}
          </span>
        )}

        <div className="ml-auto flex items-center gap-1 pl-1.5 shrink-0">
          {value && (
            <span
              onClick={handleClear}
              className="w-4 h-4 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 text-xs cursor-pointer hover:bg-red-50 hover:text-red-400 transition-colors"
            >×</span>
          )}
          <ChevronDown size={12} className={`text-gray-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
        </div>
      </div>

      {/* Dropdown */}
      {open && (
        <div className="absolute top-full left-0 mt-1.5 bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-xl z-50 w-full min-w-[220px]">

          {/* Search */}
          <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100">
            <Search size={12} className="text-slate-400 shrink-0" />
            <input
              autoFocus
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search workspace"
              className="border-none outline-none text-xs text-slate-700 w-full bg-transparent placeholder:text-slate-400"
            />
          </div>

          {/* Options */}
          <div className="max-h-60 overflow-y-auto">
            {options.length === 0 ? (
              <p className="text-center text-xs text-slate-400 py-4">Loading...</p>
            ) : filtered.length === 0 ? (
              <p className="text-center text-xs text-slate-400 py-4">No results</p>
            ) : (
              filtered.map((o, i) => {
                const idx = options.findIndex(x => x.value === o.value);
                const pal = WS_COLORS[idx % WS_COLORS.length];
                const isSelected = value === o.value;
                return (
                  <div
                    key={o.value}
                    onClick={() => handleSelect(o.value)}
                    className={`flex items-center gap-2.5 px-3 py-2 cursor-pointer transition-colors ${
                      isSelected ? "bg-violet-50" : "hover:bg-slate-50"
                    } ${i < filtered.length - 1 ? "border-b border-slate-50" : ""}`}
                  >
                    {/* Radio-style indicator */}
                    <div className={`w-4 h-4 rounded-full shrink-0 flex items-center justify-center border-2 transition-all ${
                      isSelected ? "border-violet-600 bg-violet-600" : "border-slate-300 bg-white"
                    }`}>
                      {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                    </div>

                    {/* Colored chip label */}
                    <span
                      className={`inline-flex items-center text-[11.5px] px-2.5 py-0.5 rounded-full border ${isSelected ? "font-bold" : "font-medium"}`}
                      style={{
                        background: pal.bg,
                        color: pal.color,
                        borderColor: pal.border,
                      }}
                    >
                      {o.label}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Filter SelectBox (All Team / My Tasks) ── */
function FilterSelectBox({ value, onChange, options }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const fn = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  const COLORS = { all: "#0D9488", self: "#7C3AED" };
  const selected = options.find(o => o.value === value) || options[0];
  const color = COLORS[value] || "#7C3AED";

  return (
    <div ref={ref} className="relative">
      {/* Trigger */}
      <button
        onClick={() => setOpen(p => !p)}
        className={`flex items-center gap-2 h-[38px] px-3 rounded-xl border text-xs font-semibold cursor-pointer select-none transition-all duration-150 bg-white min-w-[120px] ${
          open ? "border-violet-400 ring-2 ring-violet-100 shadow-md" : "border-gray-200 shadow-sm hover:border-violet-300 hover:shadow-md"
        }`}
      >
        <span className="w-[8px] h-[8px] rounded-full shrink-0" style={{ background: color }} />
        <span className="flex-1 text-left text-slate-700">{selected?.label}</span>
        <ChevronDown size={12} className={`text-slate-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute top-full left-0 mt-1.5 w-full bg-white border border-slate-200 rounded-xl shadow-[0_8px_24px_rgba(99,102,241,0.15)] overflow-hidden z-50">
          {options.map((opt, i) => {
            const checked = value === opt.value;
            const c = COLORS[opt.value] || "#7C3AED";
            return (
              <div
                key={opt.value}
                onClick={() => { onChange(opt.value); setOpen(false); }}
                className={`flex items-center gap-2.5 px-3 py-2.5 cursor-pointer transition-colors duration-100
                  ${checked ? "bg-violet-50" : "hover:bg-slate-50"}
                  ${i < options.length - 1 ? "border-b border-slate-100" : ""}`}
              >
                <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 transition-all ${checked ? "border-violet-600 bg-violet-600" : "border-slate-300"}`}>
                  {checked && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                </div>
                <span className="w-[8px] h-[8px] rounded-full shrink-0" style={{ background: c }} />
                <span className={`text-xs ${checked ? "font-bold text-violet-700" : "font-medium text-slate-700"}`}>{opt.label}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}